import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import type { YearMonth } from './monthParam'

const SHORT = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

const same = (a: YearMonth, b: YearMonth) => a.year === b.year && a.month === b.month

/** "jul", or "jan ’27" for a month outside the current year, so a chip is never ambiguous. */
export function chipLabel(ym: YearMonth, currentYear: number): string {
  const short = SHORT[ym.month - 1] ?? ''
  return ym.year === currentYear ? short : `${short} ’${String(ym.year).slice(2)}`
}

/**
 * D10's month strip: a row of chips, no dots. The chips are buttons with one
 * roving tab stop (the selected chip): arrows, Home and End move focus, and
 * Enter or Space selects, which is what writes `?month=`. Focus moving does not
 * select, so a keyboard user browsing the strip does not fetch every month.
 *
 * `useRovingGrid` is not used: it addresses `[data-cell]` cells of an ARIA
 * grid, and this is one row of buttons in a `nav`.
 *
 * The scrollbar is hidden and the strip snaps; on mount the selected chip is
 * scrolled to the centre of the strip (not the page) with `scrollLeft`, so the
 * page itself never jumps.
 */
export function MonthStrip({
  months,
  selected,
  currentYear,
  onSelect,
}: {
  months: YearMonth[]
  selected: YearMonth
  currentYear: number
  onSelect: (ym: YearMonth) => void
}) {
  const target = useHitTarget('page')
  const listRef = useRef<HTMLUListElement>(null)
  // A `?month=` outside the window marks no chip; the first chip then holds the tab stop.
  const selectedIndex = months.findIndex((m) => same(m, selected))
  const home = Math.max(selectedIndex, 0)
  // The roving tab stop is React state, never a DOM write: a re-render would
  // otherwise restore `tabIndex` from props and leave two chips (or none) at 0.
  // It follows the arrows, and snaps back to the selected chip whenever the
  // selection changes (a click, or Enter on the focused chip).
  const [tabStop, setTabStop] = useState(home)
  const [selectedKey, setSelectedKey] = useState(`${selected.year}-${selected.month}`)
  const key = `${selected.year}-${selected.month}`
  if (key !== selectedKey) {
    setSelectedKey(key)
    setTabStop(home)
  }

  useLayoutEffect(() => {
    const list = listRef.current
    const chip = list?.querySelectorAll<HTMLElement>('button')[tabStop]
    if (!list || !chip) return
    list.scrollLeft = chip.offsetLeft - (list.clientWidth - chip.offsetWidth) / 2
    // Mount only: re-centring on every selection would yank the strip under the pointer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])]
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (at === -1) return
    const next =
      e.key === 'ArrowRight'
        ? Math.min(at + 1, buttons.length - 1)
        : e.key === 'ArrowLeft'
          ? Math.max(at - 1, 0)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? buttons.length - 1
              : null
    if (next === null) return
    e.preventDefault()
    setTabStop(next)
    buttons[next]?.focus()
  }

  return (
    <nav aria-label="Month" className="rec-strip">
      <ul ref={listRef} className="rec-strip__list" onKeyDown={onKeyDown}>
        {months.map((m, i) => {
          const isSelected = i === selectedIndex
          return (
            <li key={`${m.year}-${m.month}`} className="rec-strip__item">
              <button
                type="button"
                className={`rec-chip hoverable${isSelected ? ' rec-chip--selected' : ''}`}
                style={{ minHeight: target, minWidth: target }}
                aria-current={isSelected ? 'date' : undefined}
                tabIndex={i === tabStop ? 0 : -1}
                onClick={() => {
                  setTabStop(i)
                  onSelect(m)
                }}
              >
                {chipLabel(m, currentYear)}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
